import React from 'react';
import { History, CheckCircle, XCircle, Clock, MessageSquare } from 'lucide-react';
import { useSmsRechargeHistory } from '../hooks/useSmsQuota';

export default function SmsRechargeHistory() {
  const { data: historyData, isLoading, error } = useSmsRechargeHistory(10);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600 dark:text-red-300" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-300" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400 dark:text-gray-300" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-700 bg-green-50 dark:text-green-200 dark:bg-green-900/40 dark:border dark:border-green-500/50';
      case 'failed':
        return 'text-red-700 bg-red-50 dark:text-red-200 dark:bg-red-900/40 dark:border dark:border-red-500/60';
      case 'pending':
        return 'text-yellow-700 bg-yellow-50 dark:text-yellow-200 dark:bg-yellow-900/40 dark:border dark:border-yellow-500/60';
      default:
        return 'text-gray-600 bg-gray-50 dark:text-gray-200 dark:bg-gray-800/60 dark:border dark:border-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 border border-gray-100 dark:border-gray-800">
        <div className="flex items-center mb-4">
          <History className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recharge History</h3>
        </div>
        <div className="animate-pulse space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 border border-gray-100 dark:border-gray-800">
        <div className="flex items-center mb-4">
          <History className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recharge History</h3>
        </div>
        <div className="text-red-600 dark:text-red-300 text-center py-8">
          Failed to load recharge history
        </div>
      </div>
    );
  }

  const history = historyData?.data || [];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 border border-gray-100 dark:border-gray-800">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <History className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-300" />
          Recharge History
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {history.length} transaction{history.length !== 1 ? 's' : ''}
        </span>
      </div>

      {history.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-300">No recharge transactions found</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Your SMS recharge history will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {history.map((transaction) => (
            <div
              key={transaction.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  {getStatusIcon(transaction.status)}
                  <span className={`ml-2 text-sm font-medium px-2 py-1 rounded-full ${getStatusColor(transaction.status)}`}>
                    {transaction.status_text}
                  </span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(transaction.created_at)}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Quantity:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                    {transaction.sms_quantity} SMS
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-300">Amount:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(transaction.amount)}
                  </span>
                </div>
              </div>

              {transaction.mpesa_receipt_number && (
                <div className="mt-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-300">Receipt:</span>
                  <span className="ml-2 font-mono text-gray-900 dark:text-gray-100">
                    {transaction.mpesa_receipt_number}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Showing last {history.length} transactions
          </p>
        </div>
      )}
    </div>
  );
} 